import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Color tokens
const C = {
  bg:              "var(--pay-bg)",
  surface:         "var(--pay-surface)",
  surfaceSecondary:"var(--pay-surface-2)",
  border:          "var(--pay-border)",
  text:            "var(--pay-text)",
  muted:           "var(--pay-text-muted)",
  purple:          "#8660F8",
};

const ClaimDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const row = location.state?.claim || location.state?.row || {};
  
  const claim = {
    id: row.claimId || row.id || `#RBM-${Date.now()}`,
    submittedDate: row.submittedDate || row.paymentDate || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    employeeName: row.staffName || row.employeeName || "Unknown Employee",
    employeeId: row.employeeId || "Unknown ID",
    designation: row.designation || row.role || "Engineer",
    department: row.department || "Engineering",
    branch: row.branch || row.branchName || "Bangalore",
    amount: Number(row.salaryCountDays?.reimburseAmount || row.amount || 0),
    expenseType: row.expenseType || "Other",
    policyStatus: row.policyCheck === "exceeded" ? "exceeded" : (row.policyCheck === "near" ? "near" : "within"),
    exceededBy: row.exceededBy || (row.policyCheck === "exceeded" ? Math.abs(row.amount - (row.monthlyCap || 5000)) : 0),
    eligibleAmount: row.eligibleAmount || (row.policyCheck === "within" ? (row.amount || 0) : (row.monthlyCap || 5000) - (row.usedBefore || 0)),
    usedBefore: row.usedBefore || 1200,
    monthlyCap: row.monthlyCap || 5000,
    journey: row.journey || `${row.branch || "Bangalore"} → Client Location`,
    purpose: row.purpose || row.description || "Business Meeting",
    paymentMode: row.paymentMode || "Personal Spend",
    date: row.date || row.submittedDate || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    description: row.description || row.notes || "No description provided.",
  };

  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [partialModalOpen, setPartialModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [partialAmount, setPartialAmount] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalNote, setApprovalNote] = useState('');

  const formatAmount = (amount) => {
    const num = Number(amount) || 0;
    return num.toLocaleString("en-IN");
  };

  const handleApproveFull = () => {
    toast.success(`Claim approved for ₹${formatAmount(claim.amount)}`);
    setApproveModalOpen(false);
  };

  const handlePartialApprove = () => {
    if (!partialAmount) { toast.error("Please enter an amount"); return; }
    toast.success(`Claim partially approved for ₹${formatAmount(parseFloat(partialAmount))}`);
    setPartialModalOpen(false);
    setPartialAmount('');
  };

  const handleReject = () => {
    if (!rejectionReason) { toast.error("Please provide a rejection reason"); return; }
    toast.error(`Claim rejected: ${rejectionReason}`);
    setRejectModalOpen(false);
    setRejectionReason('');
  };

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css" />
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Outfit', sans-serif; background: #050505; color: #fff; font-size: 12px; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #050505; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
        .back-btn { transition: transform 0.2s, box-shadow 0.2s; }
        .back-btn:hover { transform: scale(1.08); box-shadow: 0 0 20px rgba(134,96,248,0.5); }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "'Outfit', sans-serif" }}>
        <main style={{ padding: 20, maxWidth: 1400, margin: "0 auto" }}>

          {/* ── PAGE HEADER ── */}
          <section style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>

              {/* ✅ BACK BUTTON — purple circle */}
              <button
                className="back-btn"
                onClick={() => navigate(-1)}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background: C.purple,
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  cursor: "pointer",
                  flexShrink: 0,
                  boxShadow: "0 0 14px rgba(134,96,248,0.35)",
                }}
              >
                <i className="fa-solid fa-arrow-left" style={{ fontSize: 13 }}></i>
              </button>

              <div>
                <h1 style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: 0, marginBottom: 4 }}>
                  Reimbursement Claim Review
                </h1>
                <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>
                  Claim ID <span style={{ color: C.purple, fontWeight: 600 }}>{claim.id}</span> • {claim.submittedDate} • {claim.employeeName}
                </p>
              </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 12px", borderRadius: 10, fontSize: 12, fontWeight: 600, background: "rgba(245,158,11,0.1)", color: "#f59e0b", border: "1px solid rgba(245,158,11,0.2)" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#f59e0b" }}></span> Pending
              </span>
              <button onClick={() => setApproveModalOpen(true)} style={{ padding: "4px 16px", background: "#22c55e", border: "none", borderRadius: 10, fontSize: 12, fontWeight: 600, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                <i className="fa-solid fa-check" style={{ fontSize: 10 }}></i> Approve
              </button>
              <button onClick={() => setRejectModalOpen(true)} style={{ padding: "4px 16px", background: "transparent", border: "1.5px solid #ef4444", borderRadius: 10, fontSize: 12, fontWeight: 600, color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                <i className="fa-solid fa-xmark" style={{ fontSize: 10 }}></i> Reject
              </button>
              <button style={{ padding: "4px 16px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 12, fontWeight: 600, color: C.text, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                <i className="fa-solid fa-pause" style={{ fontSize: 10 }}></i> Hold
              </button>
            </div>
          </section>

          {/* ── MAIN GRID ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 16 }}>

            {/* ── LEFT COLUMN ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Employee Snapshot */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 12, background: C.purple, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700 }}>
                      {claim.employeeName?.charAt(0) || "U"}
                    </div>
                    <div>
                      <h2 style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>{claim.employeeName}</h2>
                      <p style={{ fontSize: 10, color: C.muted }}>{claim.employeeId} • {claim.designation} • {claim.department}</p>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                    <div style={{ background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, textAlign: "center" }}>
                      <p style={{ fontSize: 9, color: C.muted, marginBottom: 4 }}>Amount</p>
                      <p style={{ fontSize: 16, fontWeight: 700, color: C.purple }}>₹{formatAmount(claim.amount)}</p>
                    </div>
                    <div style={{ background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, textAlign: "center" }}>
                      <p style={{ fontSize: 9, color: C.muted, marginBottom: 4 }}>Type</p>
                      <p style={{ fontSize: 12, fontWeight: 600, color: C.text, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                        <i className="fa-solid fa-plane" style={{ color: C.purple, fontSize: 10 }}></i> {claim.expenseType}
                      </p>
                    </div>
                    <div style={{ background: claim.policyStatus === "exceeded" ? "rgba(239,68,68,0.05)" : "rgba(34,197,94,0.05)", border: claim.policyStatus === "exceeded" ? "1px solid rgba(239,68,68,0.2)" : "1px solid rgba(34,197,94,0.2)", borderRadius: 8, padding: 10, textAlign: "center" }}>
                      <p style={{ fontSize: 9, color: C.muted, marginBottom: 4 }}>Policy</p>
                      <p style={{ fontSize: 12, fontWeight: 600, color: claim.policyStatus === "exceeded" ? "#ef4444" : "#22c55e" }}>
                        {claim.policyStatus === "exceeded" ? "Exceeded" : claim.policyStatus === "near" ? "Near" : "Within"}
                      </p>
                      {claim.policyStatus === "exceeded" && (
                        <p style={{ fontSize: 9, color: "#ef4444", marginTop: 2 }}>by ₹{formatAmount(claim.exceededBy)}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Claim Details */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fa-solid fa-route" style={{ color: C.purple, fontSize: 12 }}></i> Claim Details
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(134,96,248,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className="fa-solid fa-location-dot" style={{ color: C.purple, fontSize: 12 }}></i>
                    </div>
                    <div>
                      <p style={{ fontSize: 9, color: C.muted, marginBottom: 2 }}>Journey</p>
                      <p style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{claim.journey}</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(59,130,246,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className="fa-solid fa-bullseye" style={{ color: "#3b82f6", fontSize: 12 }}></i>
                    </div>
                    <div>
                      <p style={{ fontSize: 9, color: C.muted, marginBottom: 2 }}>Purpose</p>
                      <p style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{claim.purpose}</p>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(34,197,94,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <i className="fa-solid fa-wallet" style={{ color: "#22c55e", fontSize: 12 }}></i>
                      </div>
                      <div>
                        <p style={{ fontSize: 9, color: C.muted, marginBottom: 2 }}>Payment</p>
                        <p style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{claim.paymentMode}</p>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(245,158,11,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <i className="fa-solid fa-calendar" style={{ color: "#f59e0b", fontSize: 12 }}></i>
                      </div>
                      <div>
                        <p style={{ fontSize: 9, color: C.muted, marginBottom: 2 }}>Date</p>
                        <p style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{claim.date}</p>
                      </div>
                    </div>
                  </div>
                  <div style={{ paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                    <p style={{ fontSize: 9, color: C.muted, marginBottom: 6 }}>Description</p>
                    <div style={{ background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
                      <p style={{ fontSize: 12, color: C.text, lineHeight: 1.4 }}>{claim.description}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Receipts */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fa-solid fa-paperclip" style={{ color: C.purple, fontSize: 12 }}></i> Receipts
                </h3>
                <div style={{ background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
                  <div style={{ aspectRatio: "16/9", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                    <i className="fa-solid fa-file-invoice" style={{ fontSize: 32, color: C.muted }}></i>
                    <div style={{ position: "absolute", top: 8, right: 8 }}>
                      <span style={{ padding: "2px 8px", background: "rgba(134,96,248,0.2)", color: C.purple, fontSize: 9, fontWeight: 600, borderRadius: 4, border: `1px solid ${C.purple}30` }}>Primary</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
                  {[1, 2, 3].map((item) => (
                    <div key={item} style={{ background: C.surfaceSecondary, border: item === 1 ? `1px solid ${C.purple}` : `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", cursor: "pointer" }}>
                      <div style={{ aspectRatio: "1/1", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className={`fa-solid ${item === 1 ? 'fa-receipt' : item === 2 ? 'fa-file-lines' : 'fa-file-pdf'}`} style={{ fontSize: 16, color: item === 1 ? C.purple : item === 2 ? C.muted : "#ef4444" }}></i>
                      </div>
                      <div style={{ padding: 6, background: item === 1 ? "rgba(134,96,248,0.05)" : "transparent" }}>
                        <p style={{ fontSize: 10, fontWeight: 500, color: C.text }}>{item === 1 ? 'Receipt' : item === 2 ? 'Parking' : 'Invoice'}</p>
                        <p style={{ fontSize: 8, color: C.muted }}>{item === 1 ? '245 KB' : item === 2 ? '182 KB' : '512 KB'}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={{ padding: "6px 12px", background: C.purple, border: "none", borderRadius: 8, fontSize: 12, fontWeight: 500, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
                    <i className="fa-solid fa-download" style={{ fontSize: 10 }}></i> Download
                  </button>
                  <button style={{ padding: "6px 12px", background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, fontWeight: 500, color: C.text, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
                    <i className="fa-solid fa-expand" style={{ fontSize: 10 }}></i> View
                  </button>
                </div>
              </div>

              {/* Approval Notes */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fa-solid fa-comment-dots" style={{ color: C.purple, fontSize: 12 }}></i> Notes
                </h3>
                <textarea
                  rows={3}
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                  placeholder="Write approval or rejection note..."
                  style={{ width: "100%", background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text, outline: "none", resize: "none", fontFamily: "inherit" }}
                />
                <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                  <button style={{ padding: "4px 10px", background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 10, color: C.muted, cursor: "pointer" }}>Approved as per policy</button>
                  <button style={{ padding: "4px 10px", background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 10, color: C.muted, cursor: "pointer" }}>Partial approval</button>
                </div>
              </div>
            </div>

            {/* ── RIGHT COLUMN ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Decision Summary */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 12 }}>Decision Summary</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ textAlign: "center", padding: 8, background: C.surfaceSecondary, borderRadius: 8 }}>
                    <p style={{ fontSize: 9, color: C.muted, marginBottom: 2 }}>Claimed</p>
                    <p style={{ fontSize: 16, fontWeight: 700, color: C.text }}>₹{formatAmount(claim.amount)}</p>
                  </div>
                  <div style={{ textAlign: "center", padding: 8, background: "rgba(34,197,94,0.05)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 8 }}>
                    <p style={{ fontSize: 9, color: C.muted, marginBottom: 2 }}>Eligible</p>
                    <p style={{ fontSize: 16, fontWeight: 700, color: "#22c55e" }}>₹{formatAmount(claim.eligibleAmount)}</p>
                  </div>
                  <div style={{ textAlign: "center", padding: 8, background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8 }}>
                    <p style={{ fontSize: 9, color: C.muted, marginBottom: 2 }}>Over by</p>
                    <p style={{ fontSize: 16, fontWeight: 700, color: "#ef4444" }}>₹{formatAmount(claim.exceededBy)}</p>
                  </div>
                  <div style={{ paddingTop: 8, borderTop: `1px solid ${C.border}`, textAlign: "center" }}>
                    <p style={{ fontSize: 9, color: C.muted, marginBottom: 2 }}>Suggested</p>
                    <p style={{ fontSize: 12, fontWeight: 700, color: C.purple }}>Partial Approval</p>
                  </div>
                </div>
              </div>

              {/* Policy Utilization */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 12, display: "flex", alignItems: "center", gap: 5 }}>
                  <i className="fa-solid fa-chart-line" style={{ color: C.purple, fontSize: 10 }}></i> Policy Utilization
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 10, color: C.muted }}>Monthly Cap</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>₹{formatAmount(claim.monthlyCap)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 10, color: C.muted }}>Used Before</span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: C.text }}>₹{formatAmount(claim.usedBefore)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 10, color: C.muted }}>If Full</span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: "#ef4444" }}>₹{formatAmount(claim.usedBefore + claim.amount)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: 10, color: C.muted }}>If Eligible</span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: "#22c55e" }}>₹{formatAmount(claim.usedBefore + claim.eligibleAmount)}</span>
                  </div>
                  <div style={{ paddingTop: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: C.muted, marginBottom: 4 }}>
                      <span>Utilization</span><span>100%</span>
                    </div>
                    <div style={{ width: "100%", height: 4, background: C.surfaceSecondary, borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ width: "100%", height: "100%", background: `linear-gradient(90deg, ${C.purple}, ${C.purple}80)`, borderRadius: 2 }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Approval Flow */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 12, display: "flex", alignItems: "center", gap: 5 }}>
                  <i className="fa-solid fa-diagram-project" style={{ color: C.purple, fontSize: 10 }}></i> Approval Flow
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {[
                    { done: true,  color: "#22c55e", icon: "fa-check",   label: "Submitted",      sub: claim.employeeName },
                    { done: false, color: "#f59e0b", icon: null,          label: "Manager Review", sub: "Sarah Johnson"    },
                    { done: false, color: null,       icon: null,          label: "Finance Review", sub: "Finance Team"     },
                    { done: false, color: null,       icon: null,          label: "Payroll Release",sub: "Payroll System"   },
                  ].map((step, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: step.done ? step.color : step.color || C.surfaceSecondary, border: !step.done && !step.color ? `1.5px solid ${C.border}` : "none", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {step.done
                          ? <i className="fa-solid fa-check" style={{ color: "#fff", fontSize: 10 }}></i>
                          : <div style={{ width: step.color ? 8 : 5, height: step.color ? 8 : 5, borderRadius: "50%", background: step.color ? "#fff" : C.muted }}></div>
                        }
                      </div>
                      <div>
                        <p style={{ fontSize: 12, fontWeight: 600, color: step.done || step.color ? C.text : C.muted, margin: 0 }}>{step.label}</p>
                        <p style={{ fontSize: 9, color: C.muted, margin: 0 }}>{step.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Decision */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 12 }}>Quick Decision</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <button onClick={() => setApproveModalOpen(true)} style={{ padding: "8px 12px", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#22c55e", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                    <i className="fa-solid fa-check" style={{ fontSize: 10 }}></i> Approve Full
                  </button>
                  <button onClick={() => setPartialModalOpen(true)} style={{ padding: "8px 12px", background: "rgba(134,96,248,0.1)", border: "1px solid rgba(134,96,248,0.2)", borderRadius: 8, fontSize: 12, fontWeight: 600, color: C.purple, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                    <i className="fa-solid fa-hand-holding-dollar" style={{ fontSize: 10 }}></i> Approve Partial
                  </button>
                  <button style={{ padding: "8px 12px", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#f59e0b", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                    <i className="fa-solid fa-message" style={{ fontSize: 10 }}></i> Clarify
                  </button>
                  <button onClick={() => setRejectModalOpen(true)} style={{ padding: "8px 12px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                    <i className="fa-solid fa-xmark" style={{ fontSize: 10 }}></i> Reject
                  </button>
                </div>
              </div>

              {/* Previous Claims */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 12, display: "flex", alignItems: "center", gap: 5 }}>
                  <i className="fa-solid fa-clock-rotate-left" style={{ color: C.purple, fontSize: 10 }}></i> Previous
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    { icon: "fa-gas-pump", iconColor: "#3b82f6", label: "Fuel",   date: "12 Apr", amount: "₹950",   status: "Approved" },
                    { icon: "fa-utensils", iconColor: "#22c55e", label: "Food",   date: "08 Apr", amount: "₹850",   status: "Approved" },
                    { icon: "fa-plane",    iconColor: C.purple,  label: "Travel", date: "02 Apr", amount: "₹2,750", status: "Approved" },
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 8, background: C.surfaceSecondary, borderRadius: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <i className={`fa-solid ${item.icon}`} style={{ color: item.iconColor, fontSize: 10 }}></i>
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 500, color: C.text, margin: 0 }}>{item.label}</p>
                          <p style={{ fontSize: 8, color: C.muted, margin: 0 }}>{item.date}</p>
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: C.text, margin: 0 }}>{item.amount}</p>
                        <span style={{ fontSize: 8, color: "#22c55e" }}>{item.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ── APPROVE MODAL ── */}
      {approveModalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setApproveModalOpen(false)}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, width: "100%", maxWidth: 400, padding: 20 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Approve Claim</h3>
              <button onClick={() => setApproveModalOpen(false)} style={{ width: 28, height: 28, borderRadius: 6, background: C.surfaceSecondary, border: "none", color: C.muted, cursor: "pointer" }}>
                <i className="fa-solid fa-xmark" style={{ fontSize: 12 }}></i>
              </button>
            </div>
            <p style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Select approval type for this claim</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={handleApproveFull} style={{ width: "100%", padding: "12px 16px", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 10, textAlign: "left", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 2 }}>Approve Full Amount</p>
                    <p style={{ fontSize: 10, color: C.muted }}>Approve ₹{formatAmount(claim.amount)}</p>
                  </div>
                  <i className="fa-solid fa-check-circle" style={{ color: "#22c55e", fontSize: 18 }}></i>
                </div>
              </button>
              <button onClick={() => { setApproveModalOpen(false); setPartialModalOpen(true); }} style={{ width: "100%", padding: "12px 16px", background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, textAlign: "left", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 2 }}>Approve Partial Amount</p>
                    <p style={{ fontSize: 10, color: C.muted }}>Enter custom amount</p>
                  </div>
                  <i className="fa-solid fa-hand-holding-dollar" style={{ color: "#3b82f6", fontSize: 18 }}></i>
                </div>
              </button>
            </div>
            <button onClick={() => setApproveModalOpen(false)} style={{ width: "100%", marginTop: 12, padding: "8px 12px", background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 12, fontWeight: 500, color: C.text, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── PARTIAL MODAL ── */}
      {partialModalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setPartialModalOpen(false)}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, width: "100%", maxWidth: 400, padding: 20 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Partial Approval</h3>
              <button onClick={() => setPartialModalOpen(false)} style={{ width: 28, height: 28, borderRadius: 6, background: C.surfaceSecondary, border: "none", color: C.muted, cursor: "pointer" }}>
                <i className="fa-solid fa-xmark" style={{ fontSize: 12 }}></i>
              </button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 4 }}>Claimed Amount</label>
              <input type="text" value={`₹${formatAmount(claim.amount)}`} disabled style={{ width: "100%", background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.muted }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 4 }}>Approved Amount</label>
              <input type="number" value={partialAmount} onChange={(e) => setPartialAmount(e.target.value)} placeholder="Enter approved amount" style={{ width: "100%", background: C.surfaceSecondary, border: `1px solid ${C.purple}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text, outline: "none" }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 4 }}>Reason</label>
              <textarea rows={2} placeholder="Enter reason..." style={{ width: "100%", background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text, outline: "none", resize: "none", fontFamily: "inherit" }}></textarea>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setPartialModalOpen(false)} style={{ flex: 1, padding: "8px 12px", background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, fontWeight: 500, color: C.text, cursor: "pointer" }}>Cancel</button>
              <button onClick={handlePartialApprove} style={{ flex: 1, padding: "8px 12px", background: "#3b82f6", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 500, color: "#fff", cursor: "pointer" }}>Approve</button>
            </div>
          </div>
        </div>
      )}

      {/* ── REJECT MODAL ── */}
      {rejectModalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setRejectModalOpen(false)}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, width: "100%", maxWidth: 400, padding: 20 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Reject Claim</h3>
              <button onClick={() => setRejectModalOpen(false)} style={{ width: 28, height: 28, borderRadius: 6, background: C.surfaceSecondary, border: "none", color: C.muted, cursor: "pointer" }}>
                <i className="fa-solid fa-xmark" style={{ fontSize: 12 }}></i>
              </button>
            </div>
            <div style={{ background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, padding: 12, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ color: "#ef4444", fontSize: 12, marginTop: 1 }}></i>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "#ef4444", marginBottom: 2 }}>Warning</p>
                  <p style={{ fontSize: 10, color: C.muted }}>This action cannot be undone.</p>
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 4 }}>Rejection Reason <span style={{ color: "#ef4444" }}>*</span></label>
              <textarea rows={3} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Enter mandatory rejection reason..." style={{ width: "100%", background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text, outline: "none", resize: "none", fontFamily: "inherit" }}></textarea>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setRejectModalOpen(false)} style={{ flex: 1, padding: "8px 12px", background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, fontWeight: 500, color: C.text, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleReject} style={{ flex: 1, padding: "8px 12px", background: "#ef4444", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 500, color: "#fff", cursor: "pointer" }}>Reject</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
    </>
  );
};

export default ClaimDetails;